import { ActivationState, Client, type IMessage } from '@stomp/stompjs'
import { nanoid } from 'nanoid'
import { SvelteMap, SvelteSet } from 'svelte/reactivity'

type QueuedMessage = { url: string; data: Record<string, unknown> }
type QueuedSubscription = { url: string; callback: (message: IMessage) => void }

/**
 * How to use:
 * import {webSocket} from "../websocket.svelte";
 *
 * webSocket.connect();
 * webSocket.subscribe(<url as string>, <callback function>);
 * ...
 * webSocket.sendMessage(<url as string>, <payload as object>)
 */

const MAX_RECONNECT_ATTEMPTS = 5

export class WebSock {
	identifier: string
	private readonly tempId: string
	connected: boolean | undefined
	stompClient: Client | null
	private connectAttempts: number
	private canSendMessage: boolean

	private messagesToSend: QueuedMessage[]
	private subscriptionRequests: QueuedSubscription[]

	isConnected: boolean

	/** requestId → stream destination (e.g. '/return/markers') */
	activeRequests: SvelteMap<string, string> = new SvelteMap()
	/** Destinations with ≥1 active request. Derived from activeRequests. */
	busyStreams: SvelteSet<string> = $derived.by(() => new SvelteSet(this.activeRequests.values()))

	/** Per-request failsafe timers (non-reactive — only contains setTimeout handles). */
	private requestTimers: SvelteMap<string, ReturnType<typeof setTimeout>> = new SvelteMap()
	private static readonly STALL_TIMEOUT_MS = 30_000

	markRequestStart(stream: string, requestId: string) {
		this.activeRequests.set(requestId, stream)
		this.armFailsafe(requestId, stream)
	}

	markRequestEnd(requestId: string) {
		this.clearFailsafe(requestId)
		this.activeRequests.delete(requestId)
	}

	/** Stable public predicate: "is this stream currently busy (≥1 active request)?". Reactive consumers get Svelte tracking via busyStreams. */
	isStreamBusy(stream: string): boolean {
		return this.busyStreams.has(stream)
	}

	/**
	 * Generate a requestId, register it, and send the STOMP message with requestId included in the payload.
	 * Returns the requestId so callers that need to correlate can keep it.
	 */
	startStreamRequest(stream: string, path: string, data: Record<string, unknown>): string {
		const requestId = crypto.randomUUID()
		this.markRequestStart(stream, requestId)
		this.sendMessage(path, { ...data, requestId })
		return requestId
	}

	/**
	 * Cancel an in-flight stream request: tells the backend to abort the workers
	 * for `requestId` and releases the local registry entry so {@link busyStreams}
	 * drops immediately. Late frames already in transit are filtered out by the
	 * subscription callback's own requestId check.
	 */
	stopStreamRequest(path: string, requestId: string) {
		if (!requestId) return
		this.sendMessage(path, { requestId })
		this.markRequestEnd(requestId)
	}

	/**
	 * Handle an incoming stream message: end the request on endMessage, otherwise reset its stall timer.
	 * When `requestId` is absent (degraded server), touch every active request on this stream instead.
	 */
	onStreamMessage(stream: string, requestId: string | undefined, endMessage: boolean | undefined) {
		if (requestId) {
			if (endMessage) {
				this.markRequestEnd(requestId)
			} else {
				this.touchRequest(requestId)
			}
			return
		}
		for (const [id, s] of this.activeRequests) {
			if (s === stream) this.touchRequest(id)
		}
	}

	/** Resets the 30 s stall timer for an active request. */
	private touchRequest(requestId: string) {
		const stream = this.activeRequests.get(requestId)
		if (!stream) return
		this.armFailsafe(requestId, stream)
	}

	private armFailsafe(requestId: string, stream: string) {
		this.clearFailsafe(requestId)
		this.requestTimers.set(
			requestId,
			setTimeout(() => {
				console.warn(
					`[WebSock] stall timeout for requestId=${requestId} on stream=${stream}; auto-ending`
				)
				this.activeRequests.delete(requestId)
				this.requestTimers.delete(requestId)
			}, WebSock.STALL_TIMEOUT_MS)
		)
	}

	private clearFailsafe(requestId: string) {
		const timeout = this.requestTimers.get(requestId)
		if (timeout) {
			clearTimeout(timeout)
			this.requestTimers.delete(requestId)
		}
	}

	constructor() {
		this.identifier = $state('')
		this.tempId = $state(nanoid().toString())
		this.stompClient = $state<Client | null>(null)
		this.connected = $derived(this.stompClient?.connected)
		this.connectAttempts = $state(0)
		this.canSendMessage = $state(false)
		this.messagesToSend = $state([])
		this.subscriptionRequests = $state([])
		this.isConnected = $derived(!!this?.identifier)
	}

	connect() {
		const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://'
		this.stompClient = new Client({
			brokerURL: protocol + window.location.host + '/websocket-endpoint',
			onConnect: () => {
				this.subscribe('/return/' + this.tempId, (message: IMessage) => {
					let data
					try {
						data = JSON.parse(message.body)
					} catch {
						data = message.body
					}
					if (data.retry && this.connectAttempts < MAX_RECONNECT_ATTEMPTS) {
						this.connectAttempts++
						this.sendMessage('/websocket/requestId', { uuid: this.tempId })
						return
					}
					this.identifier = data.id
					this.canSendMessage = true

					// in case we have messages that are queued from the frontend already before
					// initialization of the websocket, we send them now
					for (const messageToSend of this.messagesToSend) {
						this.sendMessage(messageToSend.url, messageToSend.data)
					}
					this.messagesToSend = [] // clear queue

					// in case we have subscription requests from the frontend already before
					// initialization of the websocket, we subscribe to them now
					for (const subscriptionRequest of this.subscriptionRequests) {
						this.subscribe(subscriptionRequest.url, subscriptionRequest.callback)
					}
					this.subscriptionRequests = [] // clear queue
				})

				// with this, we init the connection to the backend and receive a uuid,
				// which we use to identify the client in further requests
				this.sendMessage('/websocket/requestId', { uuid: this.tempId })
			},
			onDisconnect: () => {
				this.connected = false
			},
			onStompError: (frame) => {
				console.error('Broker reported error: ' + frame.headers['message'])
				console.error('Additional details: ' + frame.body)
			},
			onChangeState: (state) => {
				if (state === ActivationState.ACTIVE) {
					this.connected = true
				}
			}
		})

		this.stompClient.activate()
		this.canSendMessage = true
	}

	private subscribe(url: string, callback: (message: IMessage) => void) {
		if (this.stompClient) {
			let destination = url
			if (this?.identifier) {
				destination += `/${this.identifier}`
			}
			this.stompClient.subscribe(destination, callback)
		}
	}

	addSubscription(url: string, callback: (message: IMessage) => void) {
		if (this.stompClient && this.connected && this.canSendMessage) {
			this.subscribe(url, callback)
		} else {
			this.subscriptionRequests = [...this.subscriptionRequests, { url, callback }]
		}
	}

	unsubscribe(url: string) {
		if (this.stompClient) {
			let destination = url
			if (this?.identifier) {
				destination += `/${this.identifier}`
			}
			this.stompClient.unsubscribe(destination)
		}
	}

	disconnect(callback: () => void = () => {}) {
		if (this.stompClient) {
			this.stompClient.deactivate().then(() => callback())
		}
		this.connected = false
	}

	/**
	 * Send a message to the backend. If the websocket is not yet initialized, the message is queued and sent once the websocket is initialized.
	 * The message is sent as JSON.
	 * Note that the id field is reserved for the identifier of the client, which is generated by the backend.
	 * @param url
	 * @param data
	 */
	sendMessage(url: string, data: Record<string, unknown>) {
		if (!url.startsWith('/websocket')) {
			if (url.startsWith('websocket')) {
				url = '/' + url
			} else {
				url = '/websocket' + url
			}
		}
		if (this.stompClient && this.stompClient.connected && this.canSendMessage) {
			if (!data?.id && this?.identifier && this.identifier !== '') {
				data = Object.assign(data, { id: this.identifier })
			}
			const payload = JSON.stringify(data)
			this.stompClient.publish({ destination: url, body: payload })
			return
		}
		this.messagesToSend = [...this.messagesToSend, { url: url, data: data }]
	}
}

export const webSocket = new WebSock()
